/* eslint-disable max-lines */
import {
    AudienceID, ChannelID, ChannelStatus, NinchatUserQueue, QueueID, RealmID,
} from 'api/types/properties'
import {
    AudienceRoute,
    ChannelRoute,
    ElseRoute,
    QueueRoute,
    RealmRoute,
    RegisteredAudienceRoute,
    TranscriptRoute,
} from './routes'
import {
    NotificationEvent as BrowserNotificationEvent,
    NotificationType,
} from 'components/BrowserNotification/types'
import {
    NinchatQueueAttributes, NinchatUserAttributes,
} from 'api/types/attributes'

import {AudioEvents} from 'utils/audio'
import {EmbedErrorEvents} from './errorEvents'
import {EmbedStorage} from 'components/Embed/types'
import {NinchatAttributes} from 'api/types'
import {NinchatEmbedConfig} from './'
import {UserPresence} from 'types/members'

export interface QueueRequest {
    audience_id: AudienceID
    metadata?: Record<string, unknown>[]
    request_time: number
}


export enum EmbedEvent {
    // common - visible for app and queue versions
    Route = 'route',
    Error = 'error',

    ActivityCount = 'activity_count',
    AudienceClosed = 'audience_closed',
    AudienceEnded = 'audience_ended',
    SessionCreated = 'session_created',
    QueueUpdated = 'queue_updated',
    ChannelUpdated = 'channel_updated',
    MembersUpdated = 'members_updated',
    ContainerInitialized = 'container_initialized',

    // app - only visible for embed app version
    AudienceAccepted = 'audience_accepted',
    AcceptAudienceError = 'accept_audience_error',
    AcceptServiceRequestError = 'accept_service_request_error',
    Notification = 'notification',

    // queue - only visible for embed queue version
    AudienceEnqueued = 'audience_enqueued',
    AudienceEnqueuedNext = 'audience_enqueued_next',
    AudienceStarted = 'audience_started',
    AudienceResumed = 'audience_resumed',
    AudienceRegistered = 'audience_registered',
    AudienceTransferred = 'audience_transferred',
    MessageReceived = 'message_received',
    MessageSent = 'message_sent',
    State = 'state',
    Close = 'close',

    // ------ INTERNAL COMMUNICATION USE ONLY - LISTENERS ARE NOT TRIGGERED FOR THOSE  ------
    // app
    AcceptAudience = 'accept_audience',
    AcceptServiceRequest = 'accept_service_request',
    CompleteServiceRequest = 'complete_service_request',
    EndConversation = 'end_conversation',

    // queue
    EmbedAudienceQueue = 'embed_audience_queue',
    EmbedClose = 'embed_close',
    EmbedHero = 'embed_hero',
    EmbedCloseCancelled = 'embed_close_cancelled',
    EmbedEscape = 'embed_escape',
    EmbedMembers = 'embed_members',
    EmbedVideo = 'embed_video',
    EmbedUI = 'embed_ui',
    EmbedChannelStatus = 'embed_channel_status',
    EmbedSSO = 'embed_sso',
    EmbedDownload = 'embed_download',
    EmbedTranslations = 'embed_translations',
    EmbedSound = 'embed_sound',
    EmbedDropdownAction = 'embed_dropdown_action',

    // RN Embed
    EmbedStorageDump = 'embed_storage_dump',
}

export const InternalEvents = [
    EmbedEvent.AcceptAudience,
    EmbedEvent.AcceptServiceRequest,
    EmbedEvent.CompleteServiceRequest,
    EmbedEvent.EndConversation,
    EmbedEvent.EmbedAudienceQueue,
    EmbedEvent.EmbedClose,
    EmbedEvent.EmbedHero,
    EmbedEvent.EmbedCloseCancelled,
    EmbedEvent.EmbedEscape,
    EmbedEvent.EmbedMembers,
    EmbedEvent.EmbedVideo,
    EmbedEvent.EmbedUI,
    EmbedEvent.EmbedChannelStatus,
    EmbedEvent.EmbedSSO,
    EmbedEvent.EmbedDownload,
    EmbedEvent.EmbedTranslations,
    EmbedEvent.EmbedSound,
    EmbedEvent.EmbedDropdownAction,
    EmbedEvent.EmbedStorageDump,
]


export {NotificationType as EmbedNotificationType}

export type BaseEvent = NinchatEmbedConfig

export interface ActivityCountEvent extends BaseEvent {
    event: EmbedEvent.ActivityCount
    activity: number
    highlights: number
}

export interface AudienceAcceptedEvent extends BaseEvent {
    event: EmbedEvent.AudienceAccepted
    audience_id: AudienceID
    metadata?: Record<string, unknown>[]
}

export interface AudienceClosedEvent extends BaseEvent {
    event: EmbedEvent.AudienceClosed
    audience_id: AudienceID
}

export interface AudienceEndedEvent extends BaseEvent {
    event: EmbedEvent.AudienceEnded
    audience_id: AudienceID
}

export interface AudienceEnqueuedEvent extends BaseEvent {
    event: EmbedEvent.AudienceEnqueued
    audience_id: AudienceID
    position: number
    queue_id: QueueID
}

export interface AudienceEnqueuedNextEvent extends BaseEvent {
    event: EmbedEvent.AudienceEnqueuedNext
    audience_id: AudienceID
    queue_id: QueueID
}

export interface AudienceStartedEvent extends BaseEvent {
    event: EmbedEvent.AudienceStarted
    audience_id: AudienceID
    channel_id: ChannelID
}

export interface AudienceResumedEvent extends BaseEvent {
    event: EmbedEvent.AudienceResumed
    channel_id: ChannelID
}

export interface AudienceRegisteredEvent extends BaseEvent {
    event: EmbedEvent.AudienceRegistered
    queue_id: QueueID
}

export interface AudienceTransferredEvent extends BaseEvent {
    audience_id: AudienceID
    audience_transferred: string
    channel_id: ChannelID
    event: EmbedEvent.AudienceTransferred
}

export interface CloseEvent extends BaseEvent {
    event: EmbedEvent.Close
}

export interface StateEvent extends BaseEvent {
    event: EmbedEvent.State
    state: 'minimized' | 'open' | 'full'
}

export interface MessageReceivedEvent extends BaseEvent {
    event: EmbedEvent.MessageReceived
    channel_id: ChannelID
}

export interface MessageSentEvent extends BaseEvent {
    event: EmbedEvent.MessageSent
    channel_id: ChannelID
}

export interface ContainerInitializedEvent extends BaseEvent {
    event: EmbedEvent.ContainerInitialized
}

export interface NotificationEvent extends BaseEvent, BrowserNotificationEvent {
    event: EmbedEvent.Notification
}

export interface RouteEvent extends BaseEvent {
    event: EmbedEvent.Route
    route: AudienceRoute | ChannelRoute | QueueRoute | TranscriptRoute | RegisteredAudienceRoute | ElseRoute | RealmRoute
}

export interface EmbedAudienceQueueEvent extends BaseEvent {
    event: EmbedEvent.EmbedAudienceQueue
    queuePosition: number
}

export interface EmbedTranslationsEvent extends BaseEvent {
    event: EmbedEvent.EmbedTranslations
    lang: string,
    translations: any
}
export interface EmbedCloseEvent extends BaseEvent {
    event: EmbedEvent.EmbedClose
    clearData?: boolean
}

export interface EmbedCloseCancelledEvent extends BaseEvent {
    event: EmbedEvent.EmbedCloseCancelled
}

export interface EmbedVideoEvent extends BaseEvent {
    event: EmbedEvent.EmbedVideo
    active: boolean
    isVideoChannel: boolean
}

export interface EmbedMembersEvent extends BaseEvent {
    event: EmbedEvent.EmbedMembers
    members: Array<{
        details: NinchatUserAttributes;
        id: string;
    }>
    channel_attrs?: NinchatAttributes.NinchatChannelAttributes
}

export interface EmbedChannelStatusEvent extends BaseEvent {
    event: EmbedEvent.EmbedChannelStatus
    channel_status?: ChannelStatus
}

export interface EmbedUIEvent extends BaseEvent {
    event: EmbedEvent.EmbedUI
    ui: EmbedStorage['ui']
}

export interface EmbedEscape extends BaseEvent {
    event: EmbedEvent.EmbedEscape
}

export interface EmbedSoundEvent extends BaseEvent {
    event: EmbedEvent.EmbedSound
    sound?: AudioEvents
    mute?: boolean
}

export interface EmbedHeroEvent extends BaseEvent {
    event: EmbedEvent.EmbedHero
    heroFullVisible: boolean
}

export interface EmbedSSOEvent extends BaseEvent {
    event: EmbedEvent.EmbedSSO
    url: string
}

export interface EmbedDownloadEvent extends BaseEvent {
    event: EmbedEvent.EmbedDownload
    url: string
    fileAttrs: NinchatAttributes.NinchatFileAttributes | null
}

export interface EmbedStorageDumpEvent extends BaseEvent {
    event: EmbedEvent.EmbedStorageDump
    storage: any
}

export interface EmbedDropdownActionEvent extends BaseEvent {
    event: EmbedEvent.EmbedDropdownAction
    name: string
}

export interface AcceptAudienceEvent extends BaseEvent {
    event: EmbedEvent.AcceptAudience
    queue_id: QueueID
    audience_id?: AudienceID
}

export interface AcceptAudienceErrorEvent extends BaseEvent {
    event: EmbedEvent.AcceptAudienceError
    queue_id: QueueID
    audience_id?: AudienceID
    reason?: string
}

export interface AcceptServiceRequestEvent extends BaseEvent {
    event: EmbedEvent.AcceptServiceRequest
    queue_id: QueueID
}

export interface AcceptServiceRequestErrorEvent extends BaseEvent {
    event: EmbedEvent.AcceptServiceRequestError
    queue_id: QueueID
    reason?: string
}

export interface CompleteServiceRequestEvent extends BaseEvent {
    event: EmbedEvent.CompleteServiceRequest
    audience_id: AudienceID
}

export interface EndConversationEvent extends BaseEvent {
    event: EmbedEvent.EndConversation
    channel_id: ChannelID
}
export interface SessionCreatedEvent extends BaseEvent {
    event: EmbedEvent.SessionCreated
    user_queues?: {
      [id: QueueID]: Pick<NinchatUserQueue, 'delivery_time' | 'queue_time' | 'realm_id'> & {
        'queue_attrs': Pick<NinchatQueueAttributes, 'name' | 'length' | 'group_id' | 'is_group' | 'unassigned_registered'>
        queue_requests?: QueueRequest[]
        unassigned_registered_time?: NinchatUserQueue['queue_time']
      }
    }
    user_channels?: {
      [id: ChannelID]: {
        channel_attrs: Pick<NinchatAttributes.NinchatChannelAttributes, 'audience_id' | 'queue_id' | 'name' | 'closed'>
        channel_status?: ChannelStatus
        realm_id?: RealmID
      }
    }
}

export interface QueueUpdatedEvent extends BaseEvent {
    event: EmbedEvent.QueueUpdated
    delivery_time?: NinchatUserQueue['delivery_time']
    queue_id: QueueID
    queue_attrs: Pick<NinchatQueueAttributes, 'name' | 'length' | 'group_id' | 'is_group' | 'unassigned_registered'>
    queue_request?: QueueRequest
    queue_time?: NinchatUserQueue['queue_time']
    unassigned_registered_time?: NinchatUserQueue['queue_time']
    realm_id?: NinchatUserQueue['realm_id']
}

export interface ChannelUpdatedEvent extends BaseEvent {
    event: EmbedEvent.ChannelUpdated
    channel_id: ChannelID
    channel_attrs: Pick<NinchatAttributes.NinchatChannelAttributes, 'audience_id' | 'queue_id' | 'name' | 'closed'>
    channel_status?: ChannelStatus
    realm_id?: RealmID
}

export interface MembersUpdatedEvent extends BaseEvent {
    event: EmbedEvent.MembersUpdated
    channel_id: ChannelID
    members: Array<{
        id: string
        presence: UserPresence
        name?: string
    }>
}

export type EmbedEvents =
    ActivityCountEvent |
    AudienceAcceptedEvent |
    AudienceClosedEvent |
    AudienceEndedEvent |
    ChannelUpdatedEvent |
    MembersUpdatedEvent |
    EndConversationEvent |
    ContainerInitializedEvent |
    SessionCreatedEvent |
    QueueUpdatedEvent |
    EmbedErrorEvents |
    NotificationEvent |
    RouteEvent |
    ContainerInitializedEvent |
    AudienceEnqueuedEvent |
    AudienceEnqueuedNextEvent |
    AudienceStartedEvent |
    AudienceResumedEvent |
    AudienceRegisteredEvent |
    AudienceTransferredEvent |
    CloseEvent |
    StateEvent |
    MessageReceivedEvent |
    MessageSentEvent |
    AcceptAudienceEvent |
    AcceptAudienceErrorEvent |
    AcceptServiceRequestEvent |
    AcceptServiceRequestErrorEvent |
    CompleteServiceRequestEvent |
    EmbedAudienceQueueEvent |
    EmbedTranslationsEvent |
    EmbedCloseEvent |
    EmbedCloseCancelledEvent |
    EmbedMembersEvent |
    EmbedChannelStatusEvent |
    EmbedVideoEvent |
    EmbedUIEvent |
    EmbedEscape |
    EmbedHeroEvent |
    EmbedSSOEvent |
    EmbedDownloadEvent |
    EmbedSoundEvent |
    EmbedDropdownActionEvent |
    EmbedStorageDumpEvent
